import {
  sanitizeEntryDataResponseBase,
  sanitizeImageDetails,
  sanitizeTags,
} from "./commonUtils";

/**
 * This function sanitizes the dataResponse object for thumbnail entries.
 *
 * @param {Object} param - An object parameter.
 * @param {Object} param.dataResponse - The raw response data from the server for the entry.
 * @returns {Object} The sanitized response object.
 */
export const sanitizeThumbnailEntryDataDetails = ({ dataResponse }) => {
  const defaultValues = {
    id: "",
    created: "",
    date_modified: "",
    appointment_date: "",
    entry_type: "",
    original_entry_id: "",
    summary: "",
    classification_tags: [],
  };

  let sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: dataResponse,
    defaultValues: defaultValues,
  });

  sanitizedResponse.classification_tags = sanitizeTags(
    sanitizedResponse.classification_tags
  );
  return sanitizedResponse;
};

/**
 * This function sanitizes the dataResponse object for thumbnail entry images.
 *
 * @param {Object} param - An object parameter.
 * @param {Object} param.dataResponse - The raw response data from the server for the image.
 * @returns {Object} The sanitized response object.
 */
export const sanitizeThumbnailEntryImage = ({ dataResponse }) => {
  const defaultValues = {
    original_image: "",
    thumbnail_image: "",
    image_details: "",
  };

  let sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: dataResponse,
    defaultValues: defaultValues,
  });

  sanitizedResponse.image_details = sanitizeImageDetails(
    sanitizedResponse.image_details
  );

  return sanitizedResponse;
};

/**
 * This function sanitizes the thumbnailEntriesResponse object.
 * If no valid thumbnailEntriesResponse is provided, it will return an empty array.
 *
 * @param {Object} param - An object parameter.
 * @param {Object[]} param.thumbnailEntriesResponse - The raw response data from the server for all thumbnail entries.
 * @returns {Object[]} An array of objects, each containing the sanitized entry data and image data.
 */
export const sanitizeThumbnailEntries = ({ thumbnailEntriesResponse }) => {
  if (!thumbnailEntriesResponse || thumbnailEntriesResponse.length === 0) {
    return [];
  }

  return thumbnailEntriesResponse.map((entry) => {
    let sanitizedEntryData = sanitizeThumbnailEntryDataDetails({
      dataResponse: entry, // Use the entire entry object directly
    });

    let sanitizedImages = entry.images.map((image) =>
      sanitizeThumbnailEntryImage({ dataResponse: image })
    );

    return { ...sanitizedEntryData, images: sanitizedImages }; // Spread the sanitizedEntryData properties into the returned object
  });
};
